import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, Users, Package, Image as ImageIcon, LogOut, X, Shield, ArrowLeft, Bot } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

interface AdminSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AdminSidebar({ isOpen, onClose }: AdminSidebarProps) {
  const { signOut, profile } = useAuth();
  const location = useLocation();

  const navItems = [
    { path: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true },
    { path: '/admin/users', label: 'Nutzer', icon: Users },
    { path: '/admin/products', label: 'Produkte', icon: Package },
    { path: '/admin/media', label: 'Medien', icon: ImageIcon },
    { path: '/admin/ai', label: 'AI Agent', icon: Bot },
  ];

  const isActive = (path: string, exact?: boolean) => {
    if (exact) return location.pathname === path;
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const handleSignOut = async () => {
    await signOut();
  };

  const handleNavClick = () => {
    onClose();
  };

  return (
    <>
      <aside
        className={`
          fixed top-0 left-0 h-full bg-slate-900 border-r border-slate-800 z-50
          transition-transform duration-300 lg:translate-x-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          w-64 flex flex-col
        `}
      >
        <div className="p-6 flex items-center justify-between border-b border-slate-800">
          <Link to="/admin" className="flex items-center gap-3" onClick={handleNavClick}>
            <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-orange-500 rounded-xl flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="text-lg font-bold text-white">MAMAL</span>
              <span className="ml-2 text-xs font-medium text-orange-400 bg-orange-400/10 px-2 py-0.5 rounded-full">
                Admin
              </span>
            </div>
          </Link>
          <button
            onClick={onClose}
            className="lg:hidden p-2 hover:bg-slate-800 rounded-xl transition-colors"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        <nav className="flex-1 px-4 py-4 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path, item.exact);
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={handleNavClick}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-xl transition-all
                  ${
                    active
                      ? 'bg-gradient-to-r from-red-500/20 to-orange-500/20 text-orange-400 border border-orange-500/30'
                      : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                  }
                `}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-800 space-y-2">
          <Link
            to="/dashboard"
            onClick={handleNavClick}
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:bg-slate-800 hover:text-slate-200 transition-all"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Zurück zur App</span>
          </Link>

          <div className="flex items-center gap-3 px-4 py-3">
            {profile?.profile_image_url ? (
              <img
                src={profile.profile_image_url}
                alt="Profil"
                className="w-10 h-10 rounded-full object-cover flex-shrink-0 ring-2 ring-orange-500/50"
              />
            ) : (
              <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5 text-white" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">
                {profile?.full_name || 'Admin'}
              </p>
              <p className="text-xs text-orange-400">Administrator</p>
            </div>
          </div>

          <button
            onClick={handleSignOut}
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:bg-slate-800 hover:text-red-400 w-full transition-all"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">Abmelden</span>
          </button>
        </div>
      </aside>

      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden !mt-0"
          onClick={onClose}
        />
      )}
    </>
  );
}
